'use client';

import { useCallback } from 'react';

import { useRouter } from 'src/routes/hooks';

import { log } from 'src/utils/logger';

import { track } from 'src/lib/analytics/client';
import { fsCall } from 'src/actions/firestore/dispatch';

import { toOnboardingStatusWrite } from 'src/sections/onboarding/utils/onboarding-state';

import { useAuthContext } from 'src/auth/hooks';

import type { ServicePopupKind, ServicePopupCopy } from './service-popup';

const SERVICE_NAME = 'common/service-popup';
const UI_ID = 'service.popup';

/**
 * 서비스 팝업의 단추 — 닫기 · 그 화면으로 가기 · 「다시 보지 않기」. 큰 단추 · 「다시 보지 않기」는 `cta_clicked`(`service.popup` · 갈래:단추)로 남긴다.
 * 🔑 「다시 보지 않기」는 시작하기의 「나중에 할게요」와 같은 접기다 — `onboarding.status` 를 `dismissed` 로 적고(`pending` 일 때만)
 *   auth 사본을 새로 읽는다. 그 뒤로는 어느 기기에서도 초기 설정 팝업이 서지 않는다.
 * @param model `useServicePopup` 의 글 · 닫기
 * @param onboardingStatus 워크스페이스의 `onboarding.status`
 */
export function useServicePopupHandler({
  model,
  workspaceId,
  onboardingStatus,
}: {
  model: { popup: { kind: ServicePopupKind } | null; copy: ServicePopupCopy | null; close: () => void };
  workspaceId: string;
  onboardingStatus: unknown;
}) {
  const router = useRouter();
  const { checkUserSessionNow } = useAuthContext();
  const { copy, close } = model;
  const kind = model.popup?.kind ?? '';

  /** 큰 단추 — 닫은 것으로 치고 그 화면으로 간다 */
  const act = useCallback(() => {
    const href = copy?.primary.href;
    track({ name: 'cta_clicked', type: 'action', props: { cta_id: UI_ID, label: `${kind}:primary` } }, { uiId: UI_ID });
    close();
    if (href) router.push(href);
  }, [copy, close, router, kind]);

  const never = useCallback(async () => {
    track({ name: 'cta_clicked', type: 'action', props: { cta_id: UI_ID, label: `${kind}:never` } }, { uiId: UI_ID });
    close();
    const write = toOnboardingStatusWrite('dismissed', '', { status: onboardingStatus });
    if (!write || !workspaceId) return;
    try {
      const res = await fsCall('workspace.fields.update', { workspaceId, ...write });
      if (res?.ok === false) {
        log.info(SERVICE_NAME, '시작하기 접기 실패', { workspaceId });
        return;
      }
      await checkUserSessionNow?.({ force: true });
    } catch (error) {
      log.info(SERVICE_NAME, '시작하기 접기 실패', { err: (error as Error)?.message });
    }
  }, [close, kind, onboardingStatus, workspaceId, checkUserSessionNow]);

  return { close, act, never };
}
